import React from 'react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ArrowLeft, CheckCircle2, Search, X } from 'lucide-react';
import { OOCIntegrationSummary, OOCForecastAdjustment, OOCForecastCell } from '../oocUpdated.type';
import {
  useOOCHistoryLogic,
  useHistoryDetailLogic,
  TAB_LABELS,
  ROWS_PER_PAGE,
  getDetailFcClass,
} from './oocHistory.logic';
import styles from './oocHistory.module.scss';

interface IntegrationCardProps {
  item: OOCIntegrationSummary;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({ item }) => (
  <div className={styles.integrationCard}>
    <div className={styles.integrationIconCircle}>
      <CheckCircle2 size={20} />
    </div>
    <div className={styles.integrationCardBody}>
      <div className={styles.integrationCardRow}>
        <span className={styles.integrationOocId}>{item.oocId}</span>
        <span className={styles.integrationDivider}>|</span>
        <span className={styles.integrationDate}>{item.date}</span>
        <span className={styles.integrationDivider}>|</span>
        <span className={styles.integrationSubmitter}>{item.submittedBy}</span>
      </div>
      <div className={styles.integrationCardRow}>
        <span className={styles.integrationStatusBadge}>Integrated into {item.integratedIntoCycle}</span>
      </div>
      <div className={styles.integrationDetail}>
        <span className={styles.contractChips}>
          {item.contractTypes.map((ct) => (
            <span key={ct} className={styles.contractChip}>{TAB_LABELS[ct]}</span>
          ))}
        </span>
        {' '}· {item.ndcCount} NDCs · {item.reason}
      </div>
    </div>
  </div>
);

const HistoryDetailModal = observer(() => {
  const {
    historyDetailSubmission,
    isHistoryDetailLoading,
    closeHistoryDetail,
    visible,
    activeTab: _activeTab,
    setActiveTab,
    tabGroups,
    availableTabs,
    currentTab,
    currentAdjs,
    periods,
    showProbability,
  } = useHistoryDetailLogic();

  const renderFcCell = (cell: OOCForecastCell) => {
    if (cell.value === null) return <span className={styles.detailFcUnchanged}>-</span>;
    return (
      <div className={`${styles.detailFcValue} ${getDetailFcClass(cell.colorType, styles)}`}>
        {cell.previousValue !== null && cell.colorType === 'modified' && (
          <span className={styles.detailPrevValue}>was: {cell.previousValue.toLocaleString()}</span>
        )}
        {cell.value.toLocaleString()}
      </div>
    );
  };

  return (
    <Dialog
      header={historyDetailSubmission ? `${historyDetailSubmission.id} - OOC Detail` : 'Loading...'}
      visible={visible}
      onHide={closeHistoryDetail}
      className={styles.detailDialog}
      modal
      draggable={false}
      dismissableMask
    >
      {isHistoryDetailLoading ? (
        <div className={styles.loadingContainer}>
          <ProgressSpinner style={{ width: '40px', height: '40px' }} />
        </div>
      ) : historyDetailSubmission ? (
        <>
          <div className={styles.detailMeta}>
            {[
              { label: 'Reason', value: historyDetailSubmission.reason },
              { label: 'Submitted By', value: historyDetailSubmission.submittedBy },
              { label: 'Submission Date', value: historyDetailSubmission.submissionDate },
              { label: 'Planning Period', value: historyDetailSubmission.planningPeriod },
              { label: 'Integrated Into', value: historyDetailSubmission.integratedIntoCycle ?? '-' },
            ].map(({ label, value }) => (
              <div key={label} className={styles.detailMetaItem}>
                <span className={styles.detailMetaLabel}>{label}</span>
                <span className={styles.detailMetaValue}>{value}</span>
              </div>
            ))}
            <div className={styles.detailMetaItem}>
              <span className={styles.detailMetaLabel}>Status</span>
              <span className={styles.statusBadgeIntegrated}>Integrated</span>
            </div>
          </div>

          <div className={styles.detailFcLegend}>
            <span className={styles.detailLegendLabel}>Color Key:</span>
            {[
              { color: '#d4edda', label: 'New FC' },
              { color: '#ffe0b2', label: 'Modified FC' },
              { color: '#ffcdd2', label: 'Removed FC' },
              { color: '#ffffff', label: 'Unchanged', bordered: true },
            ].map(({ color, label, bordered }) => (
              <span key={label} className={styles.detailLegendItem}>
                <span className={styles.detailLegendSwatch} style={{ backgroundColor: color, ...(bordered ? { border: '1px solid #ccc' } : {}) }} />
                {label}
              </span>
            ))}
          </div>

          {availableTabs.length > 0 && (
            <>
              <div className={styles.detailTabsRow}>
                {availableTabs.map((tab) => {
                  const count = tabGroups[tab]?.length ?? 0;
                  const isActive = currentTab === tab;
                  return (
                    <button
                      key={tab}
                      className={`${styles.detailTabButton} ${isActive ? styles.detailTabButtonActive : ''}`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {TAB_LABELS[tab]}
                      <span className={`${styles.detailTabCount} ${isActive ? styles.detailTabCountActive : ''}`}>{count}</span>
                    </button>
                  );
                })}
              </div>

              {currentAdjs.length > 0 ? (
                <div className={styles.detailTableWrapper}>
                  <table className={styles.detailTable}>
                    <thead>
                      <tr>
                        <th>NDC10</th>
                        <th>NDC11</th>
                        <th>Supplier</th>
                        <th>Molecule</th>
                        <th>Concept</th>
                        <th>Pack Out</th>
                        {periods.map((p) => (
                          <th key={p} className={styles.detailFcCell}>{p}</th>
                        ))}
                        <th>Notes</th>
                        {showProbability && <th>Probability</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {currentAdjs.map((adj: OOCForecastAdjustment) => (
                        <tr key={adj.id}>
                          <td>{adj.ndc10}</td>
                          <td className={styles.detailColNdc11}>{adj.ndc11}</td>
                          <td>{adj.supplier}</td>
                          <td>{adj.molecule}</td>
                          <td>{adj.concept}</td>
                          <td>{adj.packOut}</td>
                          {adj.forecasts.map((cell, i) => (
                            <td key={i} className={styles.detailFcCell}>{renderFcCell(cell)}</td>
                          ))}
                          <td>{adj.notes || '-'}</td>
                          {showProbability && <td>{adj.probability ?? '-'}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.detailEmptyTab}>No adjustments in this tab.</div>
              )}
            </>
          )}
        </>
      ) : null}
    </Dialog>
  );
});

const OOCHistory: React.FC = () => {
  const {
    historyData,
    isHistoryLoading,
    navigateToDashboard,
    recentIntegrations,
    integratedCount,
    first,
    setFirst,
    visibleCount,
    handleRowClick,
    searchInput,
    setSearchInput,
    handleClearSearch,
  } = useOOCHistoryLogic();

  return (
    <div className={styles.historyContainer}>
      <div className={styles.pageHeader}>
        <h2>OOC History &amp; DP Cycle Integrations</h2>
      </div>

      <div>
        <h3 className={styles.sectionTitle}>Recent DP Cycle Integration - March 2026</h3>
        <div className={styles.successCallout}>
          <CheckCircle2 size={16} className={styles.calloutIcon} />
          <span>
            {integratedCount} OOC submission{integratedCount !== 1 ? 's were' : ' was'} automatically integrated into the March 2026 Demand Plan on 03/01/2026.
          </span>
        </div>
        <div className={styles.integrationCardsGrid}>
          {recentIntegrations.map((item) => (
            <IntegrationCard key={item.oocId} item={item} />
          ))}
        </div>
      </div>

      <div className={styles.tableSection}>
        <div className={styles.tableSectionHeader}>
          <h3 className={styles.sectionTitle}>All Past OOC Submissions</h3>
          <div className={styles.searchBar}>
            <Search size={15} className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search by OOC ID, NDC11, Molecule or Concept…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button className={styles.searchClear} onClick={handleClearSearch} aria-label="Clear search">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {isHistoryLoading ? (
          <div className={styles.tableLoadingContainer}>
            <ProgressSpinner style={{ width: '28px', height: '28px' }} />
            <span>{searchInput ? 'Searching…' : 'Loading history…'}</span>
          </div>
        ) : historyData.length === 0 ? (
          <div className={styles.emptySearchState}>
            {searchInput
              ? <>No OOC submissions found matching &ldquo;<strong>{searchInput}</strong>&rdquo;. Try a different OOC ID, NDC11, molecule, or concept.</>
              : 'No history records found.'}
          </div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <DataTable
                value={historyData}
                paginator
                rows={ROWS_PER_PAGE}
                first={first}
                onPage={(e) => setFirst(e.first)}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                selectionMode="single"
                onSelectionChange={(e) => {
                  const row = e.value as OOCIntegrationSummary;
                  if (row) handleRowClick(row.oocId);
                }}
              >
                <Column
                  field="oocId"
                  header="OOC ID"
                  body={(rowData: OOCIntegrationSummary) => (
                    <span className={styles.oocIdCell}>
                      {rowData.oocId}
                    </span>
                  )}
                  style={{ minWidth: '100px' }}
                />
                <Column field="date" header="Date" style={{ minWidth: '100px' }} />
                <Column field="submittedBy" header="Submitted By" style={{ minWidth: '140px' }} />
                <Column field="reason" header="Reason" style={{ minWidth: '180px' }} />
                <Column
                  header="Contract Types"
                  body={(rowData: OOCIntegrationSummary) => (
                    <span className={styles.contractChips}>
                      {rowData.contractTypes.map((ct) => (
                        <span key={ct} className={styles.contractChip}>{TAB_LABELS[ct]}</span>
                      ))}
                    </span>
                  )}
                  style={{ minWidth: '130px' }}
                />
                <Column field="ndcCount" header="# NDCs" style={{ minWidth: '70px', textAlign: 'center' }} />
                <Column
                  header="Final Status"
                  body={() => <span className={styles.statusBadgeIntegrated}>Integrated</span>}
                  style={{ minWidth: '100px' }}
                />
                <Column field="integratedIntoCycle" header="Integrated Into" style={{ minWidth: '130px' }} />
              </DataTable>
            </div>
            <div className={styles.tableFooter}>Showing {visibleCount} of {historyData.length}</div>
          </>
        )}
      </div>

      <div className={styles.actionBar}>
        <Button
          label="Back to Dashboard"
          icon={<ArrowLeft size={16} />}
          className={`p-button-text ${styles.backBtn}`}
          onClick={navigateToDashboard}
        />
      </div>

      <HistoryDetailModal />
    </div>
  );
};

export default observer(OOCHistory);
